import { NextRouter } from 'next/router';
import { getSegmentUrl } from '@/utils/getServerSidePropsUtil';
import {
  COMMON_SEO_ENROLLMENT_PARAMS,
  SC_SEEKER_IN_FACILITY_ROUTES,
  SEO_SERVICE_DETAILS,
  SEO_SERVICE_SHORT_NAMES,
  ServiceId,
  ServiceIdForMember,
} from '@/constants';

/**
 * @description This function generates an enrollment URL based on the provided parameters.
 * @param {Object} props - The properties for generating the enrollment URL.
 * @param {NextRouter} [props.router] - **[DEPRECATED]** The Next.js router object. Use @segmentId instead.
 * @param {string} [props.serviceIdForMember] - The service ID for the member.
 * @param {string} [props.serviceId] - The service ID.
 * @param {string} [props.serviceShortName] - The short name of the service.
 * @param {string} [props.serviceProfileId] - The service profile ID.
 * @param {boolean} [props.directoryFirstExperience] - Flag indicating if it's a directory-first experience.
 * @param {boolean} [props.isAdultCareEnrollment] - Flag indicating if it's an adult care enrollment.
 * @param {string} [props.segmentId] - The segment ID such as /babysitter. It should not include localization e.g. /babysitter/austin-tx.
 */
interface GenerateEnrollmentUrlProps {
  /**
   * @deprecated The `router` parameter is deprecated and will be removed in the future. We should use @segmentId instead.
   */
  router?: NextRouter;
  serviceIdForMember?: string;
  serviceId?: string;
  serviceShortName?: string;
  serviceProfileId?: string;
  directoryFirstExperience?: boolean;
  isAdultCareEnrollment?: boolean;
  segmentId?: string;
}

// eslint-disable-next-line complexity
const generateEnrollmentUrl = ({
  router,
  serviceIdForMember,
  serviceId: argServiceId = '',
  serviceShortName = '',
  serviceProfileId,
  segmentId = '',
  directoryFirstExperience = false,
  isAdultCareEnrollment = false,
}: GenerateEnrollmentUrlProps) => {
  const serviceId = argServiceId !== '' ? argServiceId : SEO_SERVICE_SHORT_NAMES[serviceShortName];
  const serviceDetails = SEO_SERVICE_DETAILS[serviceId];
  const fromSeoPage = `fromSeoPage=${COMMON_SEO_ENROLLMENT_PARAMS.fromSeoPage}`;
  const serviceIdParam = `serviceId=${serviceId}`;
  let memberServiceId = serviceIdForMember ?? SEO_SERVICE_DETAILS[serviceId].serviceIdForMemberVal;
  const segmentUrl = router?.query.segment ? getSegmentUrl(router.query) : segmentId;

  if (memberServiceId === memberServiceId.toUpperCase()) {
    memberServiceId = memberServiceId.toLowerCase();
  }

  const adultCareSegments = ['/adult-care', '/special-needs-adult-care', '/post-surgery-care'];

  if (serviceIdForMember === 'childCareLocation') {
    memberServiceId = 'dayCare';
  }
  if (adultCareSegments.includes(segmentUrl) || isAdultCareEnrollment) {
    memberServiceId = 'adultCare';
  }

  const serviceIdForMemberParam = `serviceIdForMember=${memberServiceId}`;

  const source = `source=${COMMON_SEO_ENROLLMENT_PARAMS.source}`;
  const pageSource = `pageSource=${serviceDetails.pageSource}`;
  const visitedServiceProfileId = serviceProfileId
    ? `&visitedServiceProfileId=${serviceProfileId}`
    : '';
  const enrollmentQueryParams = `${fromSeoPage}&${serviceIdParam}&${serviceIdForMemberParam}&${source}&${pageSource}${visitedServiceProfileId}`;
  const enrollmentType =
    memberServiceId.slice(memberServiceId.length - 3) === 'Job' ? 'provider' : 'seeker';

  if (isAdultCareEnrollment) {
    return `/app/enrollment/${enrollmentType}/ac?${enrollmentQueryParams}`;
  }

  if (serviceIdForMember === ServiceIdForMember.CARE_GIGS) {
    return `/app/vhp/care-kind?${enrollmentQueryParams}`;
  }

  if (directoryFirstExperience && serviceIdForMember === ServiceIdForMember.CHILD_CARE_LOCATION) {
    return `/app/business-search?${enrollmentQueryParams}`;
  }

  if (serviceIdForMember === ServiceIdForMember.HOUSING_LOCATION) {
    return `/app/business-search/senior-care?${enrollmentQueryParams}&enrollment=true`;
  }

  if (serviceIdForMember === ServiceIdForMember.SPECIAL_NEEDS) {
    return `/app/enrollment/${enrollmentType}/cc?${enrollmentQueryParams}`;
  }

  // careGigsJob and specialNeedsJob to vertical-triage enrollment flow since they dont have a dedicated flow.
  // The multi-vertical flow will be used on "/jobs, /full-time-jobs, and /part-time-jobs" segmentation pages (these are under childCareJobs):
  if (
    serviceIdForMember === ServiceIdForMember.CARE_GIGS_JOB ||
    serviceIdForMember === ServiceIdForMember.SPECIAL_NEEDS_JOB ||
    segmentUrl === '/jobs' ||
    segmentUrl === '/full-time-jobs' ||
    segmentUrl === '/part-time-jobs'
  ) {
    return `/app/vhp/vertical-triage?memberType=sitter&${enrollmentQueryParams}`;
  }

  // Job search enrollment URL
  if (enrollmentType === 'provider') {
    return `/app/enrollment/provider/mv/${serviceId}?${enrollmentQueryParams}`;
  }

  if (
    serviceId === ServiceId.SENIRCARE &&
    serviceIdForMember === ServiceIdForMember.HOUSING_LOCATION
  ) {
    // This is specific to Senior Assisted Living Communities( In facility care or OOH)
    return `/app/enrollment/${enrollmentType}/${serviceDetails.shortName}/${SC_SEEKER_IN_FACILITY_ROUTES.WHO_NEEDS_CARE}?${enrollmentQueryParams}`;
  }

  return `/app/enrollment/${enrollmentType}/${serviceDetails.shortName}?${enrollmentQueryParams}`;
};

export default generateEnrollmentUrl;
